import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { Card } from '@shared/ui/Card';
import {
  ASSIGNMENT_STATUS_OPTIONS,
  TARGET_AGENT_OPTIONS,
  assignmentStatusLabel,
  assignmentStatusTone,
  fmtDueDate,
  targetAgentLabel,
} from '@entities/meetings';

const Section = styled.section`
  & + & {
    margin-top: ${({ theme }) => theme.spacing(8)};
  }
`;

const SectionLabel = styled.div`
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const ControlBtn = styled.button`
  margin-left: ${({ theme }) => theme.spacing(2)};
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  padding: ${({ theme }) => `${theme.spacing(0.5)} ${theme.spacing(2)}`};
  border-radius: ${({ theme }) => theme.radius.pill};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};

  &:hover {
    border-color: ${({ theme }) => theme.colors.borderStrong};
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const EmptyHint = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text.secondary};
  max-width: 640px;
`;

const AssignmentRow = styled(Card)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;

  & + & {
    margin-top: ${({ theme }) => theme.spacing(2)};
  }
`;

const AssignmentText = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const AssignmentMeta = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-top: ${({ theme }) => theme.spacing(0.5)};
`;

const Right = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
`;

const StatusSelect = styled.select`
  font-size: 12px;
  padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(2)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
`;

const DateInput = styled.input`
  font-size: 12px;
  padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(2)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text.primary};

  &:focus-visible {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

const StatusTag = styled.span<{ $tone: 'success' | 'danger' | 'neutral' }>`
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  padding: ${({ theme }) => `${theme.spacing(0.5)} ${theme.spacing(2)}`};
  border-radius: ${({ theme }) => theme.radius.pill};
  color: ${({ theme, $tone }) =>
    $tone === 'neutral' ? theme.colors.text.muted : theme.colors.status[$tone]};
  background: ${({ theme, $tone }) =>
    $tone === 'neutral' ? theme.colors.surfaceHover : `${theme.colors.status[$tone]}18`};
`;

const AgentBadge = styled.span`
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  padding: ${({ theme }) => `${theme.spacing(0.5)} ${theme.spacing(2)}`};
  border-radius: ${({ theme }) => theme.radius.pill};
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.primarySoft};
`;

export interface MeetingAssignmentItem {
  id: string;
  text: string;
  status: string;
  due_date?: string | null;
  due_text?: string | null;
  assignee_name?: string | null;
  assignee_id?: string | null;
  assignee_employee_id?: string | null;
  target_agent?: string | null;
  linked_entity_type?: string | null;
  linked_entity_id?: string | null;
}

export interface MeetingAssignmentsSectionProps {
  meetingId: string;
  assignments: MeetingAssignmentItem[];
  canWrite: boolean;
  isDraft: boolean;
  currentUser?: { id: string; full_name: string } | null;
  onRematch: () => void;
  rematching: boolean;
  rematchInfo: string | null;
  onSetStatus: (id: string, status: string) => void;
  onSetDue: (id: string, dueDate: string | null) => void;
  onDispatch: (id: string, targetAgent: string) => void;
}

export function MeetingAssignmentsSection({
  assignments,
  canWrite,
  isDraft,
  currentUser,
  onRematch,
  rematching,
  rematchInfo,
  onSetStatus,
  onSetDue,
  onDispatch,
}: MeetingAssignmentsSectionProps) {
  return (
    <Section>
      <SectionLabel>
        Поручения
        {canWrite &&
          assignments.some((a) => !a.assignee_employee_id && !a.assignee_id) && (
            <ControlBtn type="button" onClick={onRematch} disabled={rematching}>
              {rematching ? 'Сопоставляю…' : 'Сопоставить исполнителей'}
            </ControlBtn>
          )}
      </SectionLabel>
      {rematchInfo && <EmptyHint>{rematchInfo}</EmptyHint>}
      {assignments.length > 0 ? (
        <div>
          {assignments.map((a) => (
            <AssignmentRow key={a.id}>
              <div>
                <AssignmentText>
                  {a.text}
                  {targetAgentLabel(a.target_agent) && (
                    <AgentBadge>→ {targetAgentLabel(a.target_agent)}</AgentBadge>
                  )}
                  {a.linked_entity_type === 'progress_instruction' && a.linked_entity_id && (
                    <AgentBadge as={Link} to={`/instructions?id=${a.linked_entity_id}`}>
                      Поручение ПД →
                    </AgentBadge>
                  )}
                  {a.linked_entity_type === 'function_request' && a.linked_entity_id && (
                    <AgentBadge as={Link} to={`/requests/${a.linked_entity_id}`}>
                      Заявка смежникам →
                    </AgentBadge>
                  )}
                </AssignmentText>
                <AssignmentMeta>
                  Ответственный: {a.assignee_name ?? 'уточнить'}
                  {a.assignee_employee_id || a.assignee_id ? ' ✓' : ''} · Срок:{' '}
                  {a.due_date ? fmtDueDate(a.due_date) : (a.due_text ?? 'уточнить')}
                </AssignmentMeta>
              </div>
              <Right>
                <StatusTag $tone={assignmentStatusTone(a.status)}>
                  {assignmentStatusLabel(a.status)}
                </StatusTag>
                {canWrite && (
                  <StatusSelect
                    value={a.target_agent ?? ''}
                    aria-label="Передать в смежный модуль"
                    title="Отправить поручение в смежный контур ОСНОВЫ"
                    onChange={(e) => {
                      if (e.target.value) {
                        onDispatch(a.id, e.target.value);
                      }
                    }}
                  >
                    <option value="">В контур…</option>
                    {TARGET_AGENT_OPTIONS.filter((o) => o.value).map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </StatusSelect>
                )}
                {canWrite && (
                  <DateInput
                    type="date"
                    defaultValue={a.due_date ?? ''}
                    aria-label="Срок поручения"
                    title="Установить/перенести срок"
                    onChange={(e) => onSetDue(a.id, e.target.value || null)}
                  />
                )}
                {(canWrite ||
                  (currentUser &&
                    (a.assignee_id === currentUser.id ||
                      (a.assignee_name &&
                        a.assignee_name.trim().toLowerCase().replace(/ё/g, 'е') ===
                          currentUser.full_name.trim().toLowerCase().replace(/ё/g, 'е'))))) && (
                  <StatusSelect
                    value=""
                    aria-label="Сменить статус поручения"
                    onChange={(e) => {
                      if (e.target.value) {
                        onSetStatus(a.id, e.target.value);
                      }
                    }}
                  >
                    <option value="">Сменить…</option>
                    {ASSIGNMENT_STATUS_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </StatusSelect>
                )}
              </Right>
            </AssignmentRow>
          ))}
        </div>
      ) : (
        <EmptyHint>
          Поручений нет{isDraft ? ' — появятся после утверждения протокола' : ''}.
        </EmptyHint>
      )}
    </Section>
  );
}
